/**
 * XP Progress Bar Component
 * Visualizes user XP progress toward next level
 */

import React from 'react';
import { calculateLevel } from '../data/achievements';

interface XPProgressBarProps {
  currentXP: number;
  totalXP?: number;
  showDetails?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const XPProgressBar: React.FC<XPProgressBarProps> = ({
  currentXP,
  totalXP,
  showDetails = true,
  size = 'md',
}) => {
  const levelInfo = calculateLevel(currentXP);
  const xpNeededForNext = levelInfo.nextXP;
  const progressPercent = levelInfo.progress;

  const sizeClasses = {
    sm: 'h-2',
    md: 'h-3',
    lg: 'h-4',
  };

  const textSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  return (
    <div className="w-full">
      {/* Level Badge */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className={`${textSizes[size]} font-bold text-indigo-600 dark:text-indigo-400`}>
            Level {levelInfo.level}
          </span>
          {levelInfo.title && (
            <span className={`${textSizes.sm} text-slate-500 dark:text-slate-400`}>
              {levelInfo.title}
            </span>
          )}
        </div>
        {showDetails && (
          <span className={`${textSizes.sm} text-slate-500 dark:text-slate-400`}>
            {currentXP} XP
          </span>
        )}
      </div>

      {/* Progress Bar */}
      <div className={`w-full ${sizeClasses[size]} bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden`}>
        <div
          className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 bg-[length:200%_100%] animate-pulse"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Next Level Info */}
      {showDetails && (
        <div className="mt-1 text-right">
          <span className={`${textSizes.sm} text-slate-400 dark:text-slate-500`}>
            {levelInfo.nextXP - currentXP} XP to Level {levelInfo.level + 1}
          </span>
        </div>
      )}

      {/* Total XP (optional) */}
      {totalXP !== undefined && totalXP > 0 && (
        <div className={`mt-2 ${textSizes.sm} text-slate-500 dark:text-slate-400`}>
          Lifetime XP: {totalXP.toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default XPProgressBar;
